import { CreditCard } from '../data/shared/CreditCard';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { CreditCardRewardsType } from 'data/shared/CreditCardRewardsTypes';
import { CardData } from 'data/types';
import { CategoryType } from 'data/shared/__autogenerated__';
import { EarnRateInfo } from 'data/types/CardRewardsEarnRates';
import { SegmentedControl } from './design-system/SegmentedControl';
import { MultiSelectPills } from './design-system/MultiSelectPills';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { categoryEmojiMap } from '../data/shared/utils/CategoryEmojiMap.web';
import {
  useCategoryEarnRatesAndCreditsV2,
  SelectedCategoriesMultiRate,
} from '../data/shared/hooks/useBestEarnRatesV2';
import { usePointValues } from '../data/shared/hooks/usePointValues';
import { flattenRewardsEarnRates } from '../data/shared/utils/flattenRewardsEarnRates';
import { CreditCardImage } from './design-system/CreditCardImage';
import { CardRewardsTooltip } from './design-system/CardRewardsTooltip';
import { CategoryEarnRatePill } from './CreditPills';
import { useScreenSize } from '../hooks/useScreenSize';
import { PromotionalType } from 'data/shared/__autogenerated__/types/PromotionalType';

type CategoryEarnRates = Record<
  CategoryType,
  {
    cardsWithCredits: EarnRateInfo[];
    sortedCardsByEarnRate: EarnRateInfo[];
  }
>;

const PopularCardIds = [
  CreditCard.CHASE_FREEDOM_UNLIMITED_CREDIT_CARD,
  CreditCard.AMERICAN_EXPRESS_GOLD_CARD,
  CreditCard.CITI_DOUBLE_CASH_CARD,
];

const MAJOR_BANKS = [
  'Chase',
  'Capital One',
  'American Express',
  'Citi',
  'Bank of America',
];

enum RecommendedCardIdType {
  ALL = 'all',
  TRAVEL = 'travel',
  CASHBACK = 'cashback',
  POPULAR = 'popular',
  NO_ANNUAL_FEE = 'no_annual_fee',
  // BUILDING_CREDIT = 'building_credit',
}

const RecommendedCardIdTypeToFilterFn = {
  [RecommendedCardIdType.ALL]: (card: CardData) => true,
  [RecommendedCardIdType.TRAVEL]: (card: CardData) =>
    ![
      CreditCardRewardsType.CASH_BACK,
      CreditCardRewardsType.GIFT_CARD,
    ].includes(card.rewardsType),
  [RecommendedCardIdType.CASHBACK]: (card: CardData) =>
    card.rewardsType === CreditCardRewardsType.CASH_BACK ||
    card.rewardsType === CreditCardRewardsType.GIFT_CARD,
  [RecommendedCardIdType.POPULAR]: (card: CardData) =>
    PopularCardIds.includes(card.id),
  // [RecommendedCardIdType.BUILDING_CREDIT]: (card: CardData) =>
  //   card.recommendedMinCreditScore && card.recommendedMinCreditScore <= '500',
  [RecommendedCardIdType.NO_ANNUAL_FEE]: (card: CardData) => !card.annualFee,
};

const RecommendedCardIdTypeToLabel = {
  [RecommendedCardIdType.ALL]: 'All cards',
  [RecommendedCardIdType.POPULAR]: 'Popular',
  [RecommendedCardIdType.TRAVEL]: 'Travel',
  [RecommendedCardIdType.CASHBACK]: 'Cashback',
  // [RecommendedCardIdType.BUILDING_CREDIT]: 'Building Credit',
  [RecommendedCardIdType.NO_ANNUAL_FEE]: 'No annual fee',
};

// Get emoji string for a category (for use in text contexts)
const getCategoryEmojiString = (category: string): string => {
  const emoji = categoryEmojiMap[category];

  // If we got a React.ReactNode (image), we need to fallback
  // For text contexts, we'll use a default emoji
  if (typeof emoji === 'string') {
    return emoji;
  }

  // If it's an image (React.ReactNode), try parent categories for string emoji
  if (emoji) {
    const segments = category.split('.');
    while (segments.length > 1) {
      segments.pop();
      const parentCategory = segments.join('.');
      const parentEmoji = categoryEmojiMap[parentCategory];
      if (typeof parentEmoji === 'string') {
        return parentEmoji;
      }
    }
  }

  return '📦';
};

// Regular spending categories (using formatters)
const SPENDING_CATEGORIES: Array<{
  value: string;
  label: string;
  icon: string;
}> = [
  CategoryType.ALL_PURCHASES,
  CategoryType.DINING,
  CategoryType.GAS,
  CategoryType.GROCERY,
  CategoryType.TRAVEL,
  CategoryType.STREAMING,
  CategoryType.DRUGSTORE,
  CategoryType.FITNESS,
].map((category) => ({
  value: category,
  label: formatSpendCategory(category),
  icon: getCategoryEmojiString(category),
}));

// How many top cards to show for each category
const TOP_CARDS_PER_CATEGORY = 25;

export const PopularCards = ({
  onAddCard,
  onRemoveCard,
  selectedCards,
  showTabs = true,
  isMobile = false,
  promotionalTypes = [],
  selectedCategoriesByCard,
  isPurchasingInUSA = true,
}: {
  onAddCard: (card: CreditCard) => void;
  onRemoveCard: (card: CreditCard) => void;
  selectedCards: CreditCard[];
  showTabs?: boolean;
  isMobile?: boolean;
  promotionalTypes?: PromotionalType[];
  selectedCategoriesByCard?: SelectedCategoriesMultiRate;
  isPurchasingInUSA?: boolean;
}) => {
  const screenSize = useScreenSize();
  const isMobileScreen = screenSize === 'mobile';
  const isTabletScreen = screenSize === 'tablet';
  const [recommendedCardType, setRecommendedCardType] =
    useState<RecommendedCardIdType>(
      !showTabs ? RecommendedCardIdType.POPULAR : RecommendedCardIdType.ALL
    );
  // Separate state for "Your wallet" toggle
  const [showYourWallet, setShowYourWallet] = useState<boolean>(
    selectedCards.length > 0
  );
  // State for how many unselected cards to show (starts at 6, increments by 12)
  const [cardsToShow, setCardsToShow] = useState<number>(6);
  // Regular category selections (no __your_wallet__)
  const [selectedCategories, setSelectedCategories] = useState<CategoryType[]>(
    []
  );

  // Track cards that were selected within PopularCards specifically
  // Initialize with cards that are already selected (they may have been selected from PopularCards before)
  const [popularCardsSelected, setPopularCardsSelected] = useState<
    Set<CreditCard>
  >(() => new Set(selectedCards));

  // Sync popularCardsSelected with selectedCards when they change externally
  // (e.g., when cards are removed from other parts of the app)
  useEffect(() => {
    setPopularCardsSelected((prev) => {
      const updated = new Set(prev);
      // Remove cards that are no longer in selectedCards
      prev.forEach((cardId) => {
        if (!selectedCards.includes(cardId)) {
          updated.delete(cardId);
        }
      });
      return updated;
    });
  }, [selectedCards]);

  // Handle "Your wallet" toggle - mutually exclusive with categories
  const handleYourWalletToggle = () => {
    if (showYourWallet) {
      // Turning off "Your wallet"
      setShowYourWallet(false);
    } else {
      // Turning on "Your wallet" - clear categories
      setShowYourWallet(true);
      setSelectedCategories([]);
    }
  };

  // Handle category changes - mutually exclusive with "Your wallet"
  const handleCategoryChange = (newCategories: string[]) => {
    const categoryValues = newCategories as CategoryType[];
    if (categoryValues.length > 0) {
      // Categories selected - turn off "Your wallet"
      setShowYourWallet(false);
    }
    setSelectedCategories(categoryValues);
  };

  const allCards = Object.values(CreditCardDataFiltered);

  // Clear "Your wallet" toggle if user removes all cards
  useEffect(() => {
    if (selectedCards.length === 0 && showYourWallet) {
      setShowYourWallet(false);
    }
  }, [selectedCards.length, showYourWallet]);

  // Auto-enable "Your wallet" when user adds their first card(s) and no categories selected
  useEffect(() => {
    if (
      selectedCards.length > 0 &&
      selectedCategories.length === 0 &&
      !showYourWallet
    ) {
      setShowYourWallet(true);
    }
  }, [selectedCards.length, selectedCategories.length, showYourWallet]);

  // Get all available cards filtered by type (including selected cards)
  const availableCards = useMemo(
    () =>
      allCards
        .filter((card) =>
          RecommendedCardIdTypeToFilterFn[recommendedCardType](card)
        )
        .map((card) => card.id),
    [allCards, recommendedCardType]
  );

  // Cards to include in earn rate calculations: availableCards + selectedCards
  // This ensures wallet calculations include all selected cards even if they don't match the current filter
  const cardsForEarnRateCalculation = useMemo(() => {
    const cardSet = new Set([...availableCards, ...selectedCards]);
    return Array.from(cardSet);
  }, [availableCards, selectedCards]);

  // Get point values for calculating best earn rates
  const [pointValueOverrides, setPointValueOverrides] = useState<
    Record<string, number>
  >({});
  const { pointValues } = usePointValues(
    cardsForEarnRateCalculation,
    false, // enableOverrides
    setPointValueOverrides,
    pointValueOverrides
  );

  // regularCategories is just selectedCategories now (no __your_wallet__)
  const regularCategories = selectedCategories;

  // Get relevant categories from SPENDING_CATEGORIES
  const relevantCategories = useMemo(
    () => SPENDING_CATEGORIES.map((cat) => cat.value as CategoryType),
    []
  );

  // Always include 'allPurchases' in the categories for the hook so we can use it as fallback
  // Also include all relevant categories for wallet comparisons
  const categoriesForHook = useMemo(() => {
    const categories: CategoryType[] = [
      ...regularCategories,
      ...relevantCategories,
    ];
    // Remove duplicates
    return Array.from(new Set(categories)) satisfies CategoryType[];
  }, [regularCategories, relevantCategories]);

  // Use the hook to get best earn rates for all relevant categories
  // Include all selected cards in the calculation so wallet comparisons are accurate
  const categoryEarnRates: CategoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    cardsForEarnRateCalculation,
    categoriesForHook,
    pointValues,
    isPurchasingInUSA,
    selectedCategoriesByCard,
    promotionalTypes
  );

  // Helper function to get a card's totalValue for a given category using categoryEarnRates
  const getCardTotalValueForCategory = useCallback(
    (cardId: CreditCard, category: CategoryType): number => {
      const categoryData = categoryEarnRates[category];
      if (!categoryData || !categoryData.sortedCardsByEarnRate) {
        return 0;
      }

      // Find this card in the sorted list for this category
      const cardEntry = categoryData.sortedCardsByEarnRate.find(
        (entry: EarnRateInfo) => entry.card === cardId
      );

      return cardEntry?.totalValue || 0;
    },
    [categoryEarnRates]
  );

  // Calculate wallet's totalValue for each relevant category
  const walletTotalValuesByCategory = useMemo(() => {
    const walletValues = new Map<CategoryType, number>();

    if (selectedCards.length === 0) {
      return walletValues;
    }

    relevantCategories.forEach((category) => {
      let bestTotalValue = 0;

      selectedCards.forEach((cardId) => {
        const cardTotalValue = getCardTotalValueForCategory(cardId, category);
        bestTotalValue = Math.max(bestTotalValue, cardTotalValue);
      });

      walletValues.set(category, bestTotalValue);
    });

    return walletValues;
  }, [selectedCards, relevantCategories, getCardTotalValueForCategory]);

  // For each not-selected card, compare against wallet and build map of category -> betterCards
  const betterCardsByCategory = useMemo(() => {
    const betterCardsMap = new Map<CategoryType, CreditCard[]>();

    if (selectedCards.length === 0) {
      return betterCardsMap;
    }

    // Get all not-selected cards
    const notSelectedCards = availableCards.filter(
      (cardId) => !selectedCards.includes(cardId)
    );

    relevantCategories.forEach((category) => {
      const walletTotalValue = walletTotalValuesByCategory.get(category) || 0;
      const betterCards: CreditCard[] = [];

      notSelectedCards.forEach((cardId) => {
        const cardTotalValue = getCardTotalValueForCategory(cardId, category);
        if (cardTotalValue > walletTotalValue) {
          betterCards.push(cardId);
        }
      });

      if (betterCards.length > 0) {
        betterCardsMap.set(category, betterCards);
      }
    });

    return betterCardsMap;
  }, [
    selectedCards,
    availableCards,
    relevantCategories,
    walletTotalValuesByCategory,
    getCardTotalValueForCategory,
  ]);

  // Get the set of best cards for the selected categories
  const bestCardsForCategories = useMemo(() => {
    const bestCards = new Set<CreditCard>();

    // Always include cards that were selected within PopularCards
    popularCardsSelected.forEach((cardId) => {
      if (availableCards.includes(cardId)) {
        bestCards.add(cardId);
      }
    });

    if (showYourWallet && regularCategories.length === 0) {
      // If only "Your wallet" is selected, show cards better than wallet for any category
      // If user has no cards selected, don't show any recommendations
      if (selectedCards.length === 0) {
        return bestCards;
      }

      // Use betterCardsByCategory to find cards that are better for any relevant category
      betterCardsByCategory.forEach((betterCardsForCat) => {
        betterCardsForCat.forEach((cardId) => {
          if (availableCards.includes(cardId)) {
            bestCards.add(cardId);
          }
        });
      });

      return bestCards;
    }

    if (regularCategories.length === 0) {
      return bestCards;
    }

    // For selected categories, show cards that are better than wallet
    const hasUserCards = selectedCards.length > 0;

    regularCategories.forEach((category) => {
      if (hasUserCards) {
        // Use betterCardsByCategory to find cards better than wallet for this category
        const betterCardsForCat = betterCardsByCategory.get(category) || [];
        betterCardsForCat.forEach((cardId) => {
          if (availableCards.includes(cardId)) {
            bestCards.add(cardId);
          }
        });
      } else {
        // No user cards selected - show top N cards for each category
        const categoryData = categoryEarnRates[category];
        if (categoryData && categoryData.sortedCardsByEarnRate) {
          categoryData.sortedCardsByEarnRate
            .slice(0, TOP_CARDS_PER_CATEGORY)
            .forEach((earnRateInfo: EarnRateInfo) => {
              bestCards.add(earnRateInfo.card);
            });
        }

        // Also include cards that have a dedicated earn rate for this category
        // (not just matching via allPurchases), so cash-back cards with specific
        // category bonuses aren't buried by travel cards with high cpp
        allCards.forEach((card) => {
          if (
            availableCards.includes(card.id) &&
            card.rewards_earn_rates?.some(
              (rate) =>
                !rate.requiresTravelPortal &&
                rate.earnRate > 1 &&
                rate.category === category
            )
          ) {
            bestCards.add(card.id);
          }
        });
      }
    });

    return bestCards;
  }, [
    regularCategories,
    categoryEarnRates,
    showYourWallet,
    availableCards,
    allCards,
    popularCardsSelected,
    selectedCards.length,
    betterCardsByCategory,
  ]);

  // Function to get "Better for" categories for a card
  // Shows categories where the card is better than the wallet (or good categories if no wallet)
  const getBetterForCategories = (
    card: CardData
  ): Array<{
    category: string; // Raw category key (not formatted)
    earnRate: number;
  }> => {
    const categories: Array<{
      category: string;
      earnRate: number;
    }> = [];

    const isCardSelected = selectedCards.includes(card.id);

    // If specific categories are selected, prioritize showing those categories where card is better
    if (regularCategories.length > 0) {
      regularCategories.forEach((category) => {
        // Check if card is better than wallet for this selected category
        const betterCardsForCat = betterCardsByCategory.get(category);
        const isBetterForCategory =
          betterCardsForCat && betterCardsForCat.includes(card.id);

        // Also check if card has a good rate for this category (for selected cards or when no wallet)
        const categoryData = categoryEarnRates[category];
        if (categoryData && categoryData.sortedCardsByEarnRate) {
          const cardEntry = categoryData.sortedCardsByEarnRate.find(
            (entry: EarnRateInfo) => entry.card === card.id
          );
          if (cardEntry && cardEntry.earnRate) {
            // Include if: card is better than wallet OR (no wallet and earnRate > 1) OR (card is selected)
            if (
              isBetterForCategory ||
              (selectedCards.length === 0 && cardEntry.earnRate > 1) ||
              isCardSelected
            ) {
              categories.push({
                category,
                earnRate: cardEntry.earnRate,
              });
            }
          }
        }
      });

      // If we found categories from selected categories, return them
      if (categories.length > 0) {
        return categories.sort((a, b) => b.earnRate - a.earnRate).slice(0, 3);
      }
    }

    // If no specific categories selected, or no matches found above:
    // Show categories where card is better than wallet (if wallet exists)
    if (selectedCards.length > 0 && !isCardSelected) {
      betterCardsByCategory.forEach((betterCardsForCat, category) => {
        if (betterCardsForCat.includes(card.id)) {
          // Card is in betterCardsByCategory, so it's better than wallet for this category
          // We should always be able to find it since getCardTotalValueForCategory found it
          const categoryData = categoryEarnRates[category];
          if (categoryData && categoryData.sortedCardsByEarnRate) {
            const cardEntry = categoryData.sortedCardsByEarnRate.find(
              (entry: EarnRateInfo) => entry.card === card.id
            );
            // If card is in betterCardsByCategory, it should have an entry with totalValue > 0
            // Get earnRate from entry, or calculate from totalValue if needed
            if (cardEntry) {
              // Avoid duplicates
              if (!categories.find((c) => c.category === category)) {
                // Use earnRate from entry, or calculate from totalValue if earnRate is missing
                let earnRate = cardEntry.earnRate;
                if (!earnRate && cardEntry.totalValue) {
                  const cardData = CreditCardDataFiltered[card.id];
                  const pointValue = pointValues[cardData?.rewardsType] || 1;
                  earnRate = cardEntry.totalValue / pointValue;
                }
                if (earnRate) {
                  categories.push({
                    category,
                    earnRate,
                  });
                }
              }
            } else {
              // Fallback: Card is better but entry not found - calculate from totalValue
              const totalValue = getCardTotalValueForCategory(
                card.id,
                category
              );
              if (totalValue > 0) {
                // Avoid duplicates
                if (!categories.find((c) => c.category === category)) {
                  const cardData = CreditCardDataFiltered[card.id];
                  const pointValue = pointValues[cardData?.rewardsType] || 1;
                  const estimatedEarnRate = totalValue / pointValue;
                  categories.push({
                    category,
                    earnRate: estimatedEarnRate,
                  });
                }
              }
            }
          }
        }
      });

      if (categories.length > 0) {
        return categories.sort((a, b) => b.earnRate - a.earnRate).slice(0, 3);
      }
    }

    // Fallback: Show good categories from all relevant categories
    relevantCategories.forEach((category) => {
      const categoryData = categoryEarnRates[category];
      if (categoryData && categoryData.sortedCardsByEarnRate) {
        const cardEntry = categoryData.sortedCardsByEarnRate.find(
          (entry: EarnRateInfo) => entry.card === card.id
        );
        if (cardEntry && cardEntry.earnRate && cardEntry.earnRate > 1) {
          // Avoid duplicates
          if (!categories.find((c) => c.category === category)) {
            categories.push({
              category,
              earnRate: cardEntry.earnRate,
            });
          }
        }
      }
    });

    // Sort by earn rate (highest first) and take top 3
    return categories.sort((a, b) => b.earnRate - a.earnRate).slice(0, 3);
  };

  // TODO: Calculate weak coverage categories when "Your wallet" is selected
  // This will be implemented later - calculate combo of weak categories for current wallet
  // TODO: Calculate how many weak categories each card covers (for sorting)
  // This will be implemented later - use betterCardsByCategory filtered by weak categories
  const cardWeakCategoryCoverage = useMemo(() => {
    return new Map<CreditCard, number>();
  }, []);

  // Calculate average cents per dollar across selected categories for each card
  const cardAverageCentsPerDollar = useMemo(() => {
    if (regularCategories.length === 0 || regularCategories.length === 1) {
      return new Map<CreditCard, number>();
    }

    const averageMap = new Map<CreditCard, number>();

    // Get allPurchases data as fallback
    const allPurchasesData = categoryEarnRates[CategoryType.ALL_PURCHASES];
    const allPurchasesCards = allPurchasesData?.sortedCardsByEarnRate || [];

    // For each card, find its totalValue in each selected category and average them
    bestCardsForCategories.forEach((cardId) => {
      const totalValues: number[] = [];

      regularCategories.forEach((category) => {
        let totalValue: number | undefined;

        // First, try to find the card in the specific category
        const categoryData = categoryEarnRates[category];
        if (categoryData && categoryData.sortedCardsByEarnRate) {
          // Find this card's entry in the sorted list for this category
          const cardEntry = categoryData.sortedCardsByEarnRate.find(
            (entry: EarnRateInfo) => entry.card === cardId
          );
          if (cardEntry && cardEntry.totalValue !== undefined) {
            totalValue = cardEntry.totalValue;
          }
        }

        // If not found in specific category, fall back to allPurchases
        if (totalValue === undefined) {
          const allPurchasesEntry = allPurchasesCards.find(
            (entry: EarnRateInfo) => entry.card === cardId
          );
          if (allPurchasesEntry && allPurchasesEntry.totalValue !== undefined) {
            totalValue = allPurchasesEntry.totalValue;
          }
        }

        if (totalValue !== undefined) {
          totalValues.push(totalValue);
        }
      });

      // Calculate average cents per dollar across all selected categories
      // We should have a value for each category (either specific or allPurchases fallback)
      if (totalValues.length === regularCategories.length) {
        const average =
          totalValues.reduce((sum, val) => sum + val, 0) / totalValues.length;
        averageMap.set(cardId, average);
      } else {
        // If we couldn't find a value for all categories, set to 0
        averageMap.set(cardId, 0);
      }
    });

    return averageMap;
  }, [regularCategories, categoryEarnRates, bestCardsForCategories]);

  // Combined filter function
  const filterCard = useCallback(
    (card: CardData): boolean => {
      // Don't show cards that are selected but weren't selected within PopularCards
      // (i.e., cards selected from other parts of the page)
      if (
        selectedCards.includes(card.id) &&
        !popularCardsSelected.has(card.id)
      ) {
        return false;
      }

      // Filter out cards that don't have any bonus earn rates (> 1x)
      // This applies even when no categories are selected
      if (card.rewards_earn_rates && card.rewards_earn_rates.length > 0) {
        const flattenedRates = flattenRewardsEarnRates(
          card.rewards_earn_rates,
          undefined,
          card.rewardsType
        );
        const hasBonusRate = flattenedRates.some(
          (rate) => rate.earnRate && rate.earnRate > 1
        );
        if (!hasBonusRate) {
          return false;
        }
      } else {
        // If card has no earn rates at all, filter it out
        return false;
      }

      // Check if card is in availableCards (type filter)
      if (!availableCards.includes(card.id)) {
        return false;
      }

      // Only filter by bestCardsForCategories if we have categories selected
      // (either regular categories or "__your_wallet__")
      if (selectedCategories.length > 0 || showYourWallet) {
        // Show only the best cards for the selected categories
        if (!bestCardsForCategories.has(card.id)) {
          return false;
        }
      }
      // If no categories selected and "Your wallet" is off, show all available cards that pass the other filters

      return true;
    },
    [
      selectedCards,
      popularCardsSelected,
      selectedCategories.length,
      showYourWallet,
      bestCardsForCategories,
      availableCards,
    ]
  );

  // Wrapper functions that track PopularCards selections
  const handleAddCard = useCallback(
    (cardId: CreditCard) => {
      setPopularCardsSelected((prev) => new Set(prev).add(cardId));
      onAddCard(cardId);
    },
    [onAddCard]
  );

  const handleRemoveCard = useCallback(
    (cardId: CreditCard) => {
      setPopularCardsSelected((prev) => {
        const updated = new Set(prev);
        updated.delete(cardId);
        return updated;
      });
      onRemoveCard(cardId);
    },
    [onRemoveCard]
  );

  // Bank-diverse ordering for default "All" view (no categories, no wallet)
  const defaultStateCardOrder = useMemo(() => {
    const orderMap = new Map<CreditCard, number>();
    const allPurchasesData = categoryEarnRates[CategoryType.ALL_PURCHASES];
    const qualityRanking = allPurchasesData?.sortedCardsByEarnRate || [];
    if (qualityRanking.length === 0) {
      return orderMap;
    }

    // Build a quality rank lookup (lower = better)
    const qualityRank = new Map<CreditCard, number>();
    qualityRanking.forEach((entry, idx) => qualityRank.set(entry.card, idx));

    // Build card-to-bank lookup for available cards
    const cardBank = new Map<CreditCard, string>();
    allCards.forEach((card) => {
      const bank = card.bank_name || card.issuer || '';
      cardBank.set(card.id, bank);
    });

    // Pick top-6 with bank diversity
    const PAGE_SIZE = 6;
    const seenBanks = new Set<string>();
    const top6: CreditCard[] = [];

    // 1) Best card from each major bank
    for (const bank of MAJOR_BANKS) {
      const best = qualityRanking.find(
        (entry) =>
          cardBank.get(entry.card) === bank && !top6.includes(entry.card)
      );
      if (best) {
        top6.push(best.card);
        seenBanks.add(bank);
      }
    }

    // 2) Fill remaining slots preferring unseen banks
    for (const entry of qualityRanking) {
      if (top6.length >= PAGE_SIZE) {
        break;
      }
      if (top6.includes(entry.card)) {
        continue;
      }
      const bank = cardBank.get(entry.card) || '';
      if (!seenBanks.has(bank)) {
        top6.push(entry.card);
        seenBanks.add(bank);
      }
    }
    // Still not full? Fill with next best regardless of bank
    for (const entry of qualityRanking) {
      if (top6.length >= PAGE_SIZE) {
        break;
      }
      if (top6.includes(entry.card)) {
        continue;
      }
      top6.push(entry.card);
      seenBanks.add(cardBank.get(entry.card) || '');
    }

    // Sort top-6 by quality rank (best first)
    top6.sort((a, b) => (qualityRank.get(a) ?? 0) - (qualityRank.get(b) ?? 0));

    // Assign order values: top-6 get negative values so they sort first
    top6.forEach((cardId, i) => {
      orderMap.set(cardId, -10000 + i);
    });

    // Beyond page 1: unseen-bank bonus of -3
    qualityRanking.forEach((entry, idx) => {
      if (orderMap.has(entry.card)) {
        return;
      }
      const bank = cardBank.get(entry.card) || '';
      const bonus = seenBanks.has(bank) ? 0 : -3;
      orderMap.set(entry.card, idx + bonus);
      if (bonus < 0) {
        seenBanks.add(bank);
      }
    });

    return orderMap;
  }, [categoryEarnRates, allCards]);

  // Compute filtered and sorted cards once
  const filteredAndSortedCards = useMemo(() => {
    return allCards
      .filter(filterCard)
      .map((card, index) => {
        // Calculate order for stable sorting
        // Lower order values appear first
        let order = index;

        // When "Your wallet" is selected, prioritize cards added via PopularCards
        if (showYourWallet && regularCategories.length === 0) {
          const isPopularCardSelected = popularCardsSelected.has(card.id);
          if (isPopularCardSelected) {
            // Selected cards get negative order to appear first
            // Use a stable value based on card ID to prevent jumping
            order = -10000 + (card.id.charCodeAt(0) || 0);
          } else {
            // Other cards maintain their position with coverage-based ordering
            const coverage = cardWeakCategoryCoverage.get(card.id) || 0;
            order = 1000 - coverage; // Higher coverage = lower order
          }
        } else if (regularCategories.length > 1) {
          // When multiple categories are selected, sort by average cents per dollar
          const avgCents = cardAverageCentsPerDollar.get(card.id) || 0;
          order = 10000 - avgCents; // Higher value = lower order
        } else if (regularCategories.length === 0 && !showYourWallet) {
          const diversityOrder = defaultStateCardOrder.get(card.id);
          if (diversityOrder !== undefined) {
            order = diversityOrder;
          }
        }

        return { card, order };
      })
      .sort((a, b) => {
        // Stable sort: if orders are equal, maintain original order
        if (a.order === b.order) {
          return a.card.id.localeCompare(b.card.id);
        }
        return a.order - b.order;
      })
      .map(({ card }) => card);
  }, [
    allCards,
    filterCard,
    showYourWallet,
    regularCategories.length,
    popularCardsSelected,
    cardWeakCategoryCoverage,
    cardAverageCentsPerDollar,
    defaultStateCardOrder,
  ]);

  // Cards excluding selected ones (for count and display)
  const cardsExcludingSelected = useMemo(() => {
    return filteredAndSortedCards.filter(
      (card) => !selectedCards.includes(card.id)
    );
  }, [filteredAndSortedCards, selectedCards]);

  // Combined cards: selected cards + paginated unselected cards
  const cardsToDisplay = useMemo(() => {
    const selected = filteredAndSortedCards.filter((card) =>
      selectedCards.includes(card.id)
    );
    const unselected = cardsExcludingSelected.slice(0, cardsToShow);
    return [...selected, ...unselected];
  }, [
    filteredAndSortedCards,
    selectedCards,
    cardsExcludingSelected,
    cardsToShow,
  ]);

  return (
    <div
      style={
        {
          // maxWidth: '100%',
        }
      }
    >
      <div style={{}}>
        <div style={{}}>
          {showTabs && (
            <>
              <div
                style={{
                  marginBottom: 16,
                  padding: isMobileScreen ? '0' : '0 20px',
                  display: 'flex',
                  gap: '6px',
                  flexWrap: 'wrap',
                  overflow: 'auto',
                  alignItems: 'center',
                }}
              >
                {/* "Your wallet" toggle - only show if user has cards */}
                {selectedCards.length > 0 && (
                  <>
                    {showYourWallet ? (
                      <div
                        style={{
                          padding: '2px',
                          background:
                            'linear-gradient(90deg, #ff6b9d, #c471ed, #4da9e6, #1d72b8, #4da9e6, #c471ed, #ff6b9d)',
                          backgroundSize: '300% 300%',
                          borderRadius: '8px',
                          animation: 'gradient-shift 10s ease infinite',
                          flexShrink: 0,
                        }}
                      >
                        <button
                          onClick={handleYourWalletToggle}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: '#EEF1F4',
                            color: '#1a1a1a',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'Inter, sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          <span>💳</span>
                          <span>Your wallet</span>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleYourWalletToggle}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          border: '2px solid #EEF1F4',
                          backgroundColor: '#fff',
                          color: '#666',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 400,
                          fontFamily: 'Inter, sans-serif',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        <span>💳</span>
                        <span>Your wallet</span>
                      </button>
                    )}
                  </>
                )}
                {/* Category pills */}
                <MultiSelectPills
                  options={SPENDING_CATEGORIES}
                  selectedValues={selectedCategories}
                  onChange={handleCategoryChange}
                  isMobile={isMobileScreen}
                />
              </div>
              <style>{`
                @keyframes gradient-shift {
                  0% {
                    background-position: 0% 50%;
                  }
                  50% {
                    background-position: 100% 50%;
                  }
                  100% {
                    background-position: 0% 50%;
                  }
                }
              `}</style>
              <div
                style={{
                  marginBottom: 16,
                  padding: isMobileScreen ? '0' : '0 20px',
                }}
              >
                <SegmentedControl
                  options={[
                    {
                      value: RecommendedCardIdType.ALL,
                      label:
                        RecommendedCardIdTypeToLabel[RecommendedCardIdType.ALL],
                    },
                    {
                      value: RecommendedCardIdType.TRAVEL,
                      label:
                        RecommendedCardIdTypeToLabel[
                          RecommendedCardIdType.TRAVEL
                        ],
                    },
                    {
                      value: RecommendedCardIdType.CASHBACK,
                      label:
                        RecommendedCardIdTypeToLabel[
                          RecommendedCardIdType.CASHBACK
                        ],
                    },
                    {
                      value: RecommendedCardIdType.NO_ANNUAL_FEE,
                      label:
                        RecommendedCardIdTypeToLabel[
                          RecommendedCardIdType.NO_ANNUAL_FEE
                        ],
                    },
                  ]}
                  value={recommendedCardType}
                  onChange={(value) =>
                    setRecommendedCardType(value as RecommendedCardIdType)
                  }
                />
              </div>
            </>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobileScreen
              ? '1fr'
              : isTabletScreen
              ? 'repeat(2, 1fr)'
              : 'repeat(3, 1fr)',
            gap: '12px 14px',
            padding: isMobileScreen ? '0' : '0 20px',
          }}
        >
          {cardsToDisplay.map((card) => {
            const betterForCategories = getBetterForCategories(card);
            const isSelected = selectedCards.includes(card.id);
            return (
              <CardRewardsTooltip key={card.id} card={card} position="top">
                <div
                  onClick={(e) => {
                    // Don't trigger card selection when clicking checkbox
                    if (
                      (e.target as HTMLElement).tagName !== 'INPUT' &&
                      (e.target as HTMLElement).tagName !== 'LABEL'
                    ) {
                      if (isSelected) {
                        handleRemoveCard(card.id);
                      } else {
                        handleAddCard(card.id);
                      }
                    }
                  }}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '14px',
                    alignItems: 'flex-start',
                    border: isSelected
                      ? '1px solid #4DA9E6'
                      : '1px solid #e5e7eb',
                    transition: 'border-color 0.2s ease',
                    opacity: isSelected ? 1 : 1,
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  {/* Checkbox */}
                  <div style={{ flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          handleAddCard(card.id);
                        } else {
                          handleRemoveCard(card.id);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#4DA9E6',
                      }}
                    />
                  </div>

                  {/* Card Image */}
                  <div style={{ flexShrink: 0 }}>
                    <CreditCardImage
                      imageName={card.imageName}
                      alt={card.name}
                      width={80}
                      maxHeight={52}
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        borderRadius: '6px',
                      }}
                    />
                  </div>

                  {/* Card Info */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    {/* Card Name */}
                    <h3
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: 400,
                        fontFamily: 'Merriweather, serif',
                        color: '#000',
                        lineHeight: 1.2,
                      }}
                    >
                      {card.name}
                    </h3>

                    {/* Category earn rate pills */}
                    {(() => {
                      // If a single category is selected, show pill for that category
                      if (regularCategories.length === 1) {
                        const selectedCategory = regularCategories[0];
                        const categoryData =
                          categoryEarnRates[selectedCategory];
                        const cardEntry =
                          categoryData?.sortedCardsByEarnRate.find(
                            (entry: EarnRateInfo) => entry.card === card.id
                          );

                        if (cardEntry && cardEntry.earnRate) {
                          return (
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                alignItems: 'center',
                                marginTop: '2px',
                              }}
                            >
                              <span style={{ fontSize: '13px', color: '#5D6C70' }}>
                                {isSelected || selectedCards.length === 0 ? 'Good for' : 'Earn more on'}
                              </span>
                              <CategoryEarnRatePill
                                category={selectedCategory}
                                earnRate={cardEntry.earnRate}
                                rewardsType={
                                  cardEntry.rewardsType || card.rewardsType
                                }
                                style={{ fontSize: '12px', padding: '3px 8px', marginBottom: 0 }}
                              />
                            </div>
                          );
                        }
                      }

                      // Otherwise, show pills for each better-for category
                      if (betterForCategories.length > 0) {
                        return (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '4px',
                              alignItems: 'center',
                              marginTop: '2px',
                            }}
                          >
                            <span style={{ fontSize: '13px', color: '#5D6C70' }}>
                              {isSelected || selectedCards.length === 0 ? 'Good for' : 'Earn more on'}
                            </span>
                            {betterForCategories
                              .filter(
                                (cat) =>
                                  cat &&
                                  typeof cat.category === 'string' &&
                                  typeof cat.earnRate === 'number'
                              )
                              .map((cat, index) => (
                                <CategoryEarnRatePill
                                  key={`${cat.category}-${index}`}
                                  category={cat.category}
                                  earnRate={cat.earnRate}
                                  rewardsType={card.rewardsType}
                                  style={{ fontSize: '12px', padding: '3px 8px', marginBottom: 0 }}
                                />
                              ))}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </CardRewardsTooltip>
            );
          })}
        </div>
        {cardsExcludingSelected.length > 6 && (
          <div
            style={{
              padding: isMobileScreen ? '16px 0' : '16px 20px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={() => {
                if (cardsToShow >= cardsExcludingSelected.length) {
                  // If showing all, reset to 6
                  setCardsToShow(6);
                } else {
                  // Add 12 more cards (or show all if less than 12 remaining)
                  setCardsToShow(
                    Math.min(cardsToShow + 12, cardsExcludingSelected.length)
                  );
                }
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#fff',
                color: '#1a1a1a',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#4DA9E6';
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.backgroundColor = '#fff';
              }}
            >
              {cardsToShow >= cardsExcludingSelected.length
                ? 'Show less'
                : `Show more (${
                    cardsExcludingSelected.length - cardsToShow
                  } remaining)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
